import { useContext, useEffect, useState } from "react";
import { Dropdown } from "semantic-ui-react";
import { Grid, Modal } from "antd";
import ZoomContext from '../../../../pages/ZoomViewer/context/zoom-context';
import ZoomMediaContext from '../../../../pages/ZoomViewer/context/media-context';
import { useVideo } from "../../../../pages/ZoomViewer/hooks/useVideo";
import configSource from "../../../../pages/Viewer/Images/config-source.svg";
import { get } from "../../../../utils/helpers";
import TranscoderOptions from "./TranscoderOptions";
import { useWhiteboardingStore } from "../../../../store";
import notification from "antd/lib/notification";
import { Participant } from "@zoom/videosdk";
import "./styles.scss";

const Devices = (props: any) => {
    const { showDominantSpeaker } = props;
    const zmClient = useContext(ZoomContext);
    const { mediaStream } = useContext(ZoomMediaContext);
    const { resetAllTools } = useWhiteboardingStore();
    const { isVideoOn } = useVideo(zmClient, mediaStream);
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = get(screens, 'xs', false);
    const isTablet = get(screens, 'sm', false) && !get(screens, 'xl', false);
    const useDevicesModal = isMobile || isTablet;

    const [cameraDropdownOpen, setCameraDropdownOpen] = useState(false);
    const [selectedCam, setSelectedCam] = useState(get(get(mediaStream?.getCameraList(),'0',''),'label',''))
    const currentUser = zmClient.getCurrentUserInfo();

    const toggleAudioVideo = async () => {
        setCameraDropdownOpen((prev) => !prev);
        resetAllTools();
    };

    const getCameraList = () => {
        let cameraList: any = []
        if (mediaStream) {
            const camerasList = mediaStream.getCameraList()
            camerasList.filter((device: any) => device.deviceId !== "default").forEach((camera: any) => {
                cameraList.push({
                    value: camera.deviceId,
                    text: camera.label,
                    key: camera.label,
                })
            })
        }

        cameraList.push({
            value: "DESKTOP",
            text: "Share Your Desktop",
            key: "desktop",
        });

        return cameraList;
    }

    const getMicList = () => {
        let micList: any = []
        if (mediaStream) {
            const microphonesList = mediaStream.getMicList()
            microphonesList.filter((device: any) => device.deviceId !== "default").forEach((mic: any) => {
                micList.push({
                    value: mic.deviceId,
                    text: mic.label,
                    key: mic.label,
                })
            })
        }
        return micList;
    }

    const getActiveMicrophone = () => {
        if (!mediaStream) {
            return "";
        }

        const microphonesList = mediaStream.getMicList();
        const activeMic = mediaStream.getActiveMicrophone();

        // No microphones available
        if (microphonesList.length === 0) {
            return "";
        }

        // Handle only one microphone available
        if (microphonesList.length === 1) {
            return microphonesList[0].label;
        }

        // Find the active microphone, handle 'default' device
        if (activeMic === 'default') {
            // Assumes first microphone is the default if 'default' is specified
            return microphonesList[0].label.replace("Default - ", "");
        }

        // Find microphone with matching deviceId
        const activeMicDevice = microphonesList.find((mic: any) => mic.deviceId === activeMic);

        return activeMicDevice ? activeMicDevice.label : "";
    }



    const getActiveCamera = () => {
        if (!mediaStream) {
            return "";
        }

        const camerasList = mediaStream.getCameraList();
        const activeCamera = mediaStream.getActiveCamera();

        // checking if screen share is on
        if(mediaStream.getShareStatus() === "sharing"){
            return "Share Your Desktop";
        }

        // No cameras available
        if (camerasList.length === 0) {
            return "";
        }

        // Handle only one camera available
        if (camerasList.length === 1) {
            return camerasList[0].label;
        }

        // Find the active camera, handle 'default' device
        if (activeCamera === 'default') {
            // Assumes first camera is the default if 'default' is specified
            return camerasList[0].label.replace("Default - ", "");
        }

        // Find camera with matching deviceId
        const activeCameraDevice = camerasList.find((camera: any) => camera.deviceID === activeCamera);
        return activeCameraDevice ? activeCameraDevice.label : "";
    }


    const changeMicrophone = async (e: any, v: any) => {
        console.log('v.name', v.value)
        if (mediaStream) {
            await mediaStream.switchMicrophone(v.value)
        }
        setCameraDropdownOpen(false);
    };

    const onScreenShareClick = async () => {
        if(zmClient.getAllUser().some((participant:Participant)=> participant.sharerOn === true)){
            notification.warning({
                message:'Only one person can share screen at a time',
                placement:'topRight',
                duration:2
            })
            return
        }
        const stream: any = zmClient.getMediaStream();
        if (stream?.isStartShareScreenWithVideoElement()) {
          await stream.startShareScreen(
            document.querySelector(`#screen-share-content-video`)
          )
          // screen share successfully started and rendered
        } else {
          await stream.startShareScreen(
            document.querySelector(`#screen-share-content-canvas${currentUser.userIdentity}`)
          )
        }
      };

    const changeCamera = async (e: any, v: any) => {
        if (v.value === 'DESKTOP') {
            onScreenShareClick()
        } else if (mediaStream) {
            if(mediaStream.getShareStatus() === "sharing"){
                mediaStream.stopShareScreen()
            } else {
                await mediaStream.switchCamera(v.value)
            }
        }
        setCameraDropdownOpen(false);
    };

    useEffect(() => {
        if (mediaStream?.getShareStatus() !== "sharing") {
            setSelectedCam(get(get(mediaStream?.getCameraList(),'0',''),'label',''))
        }
    }, [mediaStream])

    const dropdownContent = (
        <Dropdown.Menu className="cameraMenu">
            <Dropdown.Item>
                <Dropdown
                    className="cameraDropdown"
                    id="cameraDropdown"
                    pointing="left"
                    multiple={false}
                    fluid
                    selection
                    placeholder="camera"
                    options={getCameraList()}
                    text={getActiveCamera()}
                    onChange={changeCamera}
                    disabled={!isVideoOn}
                    value={selectedCam}
                />
            </Dropdown.Item>
            <Dropdown.Item>
                <Dropdown
                    id="cameraDropdown"
                    placeholder="microphone"
                    multiple={false}
                    fluid
                    selection
                    pointing="left"
                    text={getActiveMicrophone()}
                    onChange={changeMicrophone}
                    options={getMicList()}
                />
            </Dropdown.Item>
            <TranscoderOptions
                cameraDropdownOpen={cameraDropdownOpen}
                setCameraDropdownOpen={setCameraDropdownOpen}
            />
        </Dropdown.Menu>
    );

    const devicesModalBody = (
      <div className="devices-modal-body cameraMenu">
        <div>
          <Dropdown
            className="cameraDropdown"
            id="cameraDropdown"
            pointing="left"
            multiple={false}
            fluid
            selection
            placeholder="camera"
            options={getCameraList()}
            text={getActiveCamera()}
            onChange={changeCamera}
            disabled={!isVideoOn}
            value={selectedCam}
          />
        </div>
        <div>
          <Dropdown
            id="cameraDropdownMic"
            placeholder="microphone"
            multiple={false}
            fluid
            selection
            pointing="left"
            text={getActiveMicrophone()}
            onChange={changeMicrophone}
            options={getMicList()}
          />
        </div>
        <TranscoderOptions
          cameraDropdownOpen={cameraDropdownOpen}
          setCameraDropdownOpen={setCameraDropdownOpen}
        />
      </div>
    );

    return (
      <>
        <div
          onClick={toggleAudioVideo}
          className={`top-menu-button-item audiovisual-source-dropdown ${
            showDominantSpeaker ? "disableddiv" : ""
          }`}
        >
          <img src={configSource} alt="" />
          {!useDevicesModal && (
            <Dropdown icon="" open={cameraDropdownOpen} onClose={toggleAudioVideo} className=" ">
              {dropdownContent}
            </Dropdown>
          )}
          <span>Select Camera</span>
        </div>
        {useDevicesModal && (
          <Modal
            open={cameraDropdownOpen}
            onCancel={toggleAudioVideo}
            footer={null}
            title={null}
            closable={false}
            maskClosable={true}
            width={Math.min(320, typeof window !== 'undefined' ? window.innerWidth - 32 : 320)}
            className="devices-modal"
            wrapClassName="devices-modal-wrap"
            getContainer={() => document.body}
          >
            {devicesModalBody}
          </Modal>
        )}
      </>
    );
};

export default Devices;
